/*
 * Разбор ответов Kubernetes API для карточки «Узлы» кластера: количества ресурсов,
 * состояние узла, запросы подов по узлам и группировка узлов в пулы по метке.
 * Чистые функции без React — покрыты юнит-тестами.
 */

/** Метка пула по умолчанию: её пишет шаблон k8s-cluster в node_labels групп узлов. */
export const POOL_LABEL = 'viva.io/pool';
export const ZONE_LABEL = 'topology.kubernetes.io/zone';
export const INSTANCE_TYPE_LABEL = 'node.kubernetes.io/instance-type';
export const GPU_RESOURCE = 'nvidia.com/gpu';

/** Метки, по которым имеет смысл группировать узлы, в порядке предпочтения. */
export const GROUP_LABELS = [POOL_LABEL, ZONE_LABEL, INSTANCE_TYPE_LABEL];

const CPU_SUFFIX: Record<string, number> = { n: 1e-9, u: 1e-6, m: 1e-3, '': 1 };
const MEM_SUFFIX: Record<string, number> = {
  '': 1,
  k: 1e3,
  M: 1e6,
  G: 1e9,
  T: 1e12,
  Ki: 1024,
  Mi: 1024 ** 2,
  Gi: 1024 ** 3,
  Ti: 1024 ** 4,
};

/** CPU в ядрах: "4", "3920m", "250m", "1.5". */
export const parseCpu = (q?: string): number => {
  if (!q) return 0;
  const m = /^([0-9.]+)([a-z]*)$/i.exec(q.trim());
  if (!m) return 0;
  const factor = CPU_SUFFIX[m[2]];
  return factor === undefined ? 0 : Number(m[1]) * factor;
};

/** Память в байтах: "16372048Ki", "128Mi", "1Gi", "500M", "1024". */
export const parseMemory = (q?: string): number => {
  if (!q) return 0;
  const m = /^([0-9.]+)([A-Za-z]*)$/.exec(q.trim());
  if (!m) return 0;
  const factor = MEM_SUFFIX[m[2]];
  return factor === undefined ? 0 : Number(m[1]) * factor;
};

export const formatCores = (cores: number): string =>
  cores >= 10 ? String(Math.round(cores)) : String(Math.round(cores * 10) / 10);

export const formatGiB = (bytes: number): string => {
  const gib = bytes / 1024 ** 3;
  return gib >= 10
    ? String(Math.round(gib))
    : String(Math.round(gib * 10) / 10);
};

/** Подмножество Node из Kubernetes API, которое нужно карточке. */
export type K8sNode = {
  metadata: { name: string; labels?: Record<string, string> };
  spec?: {
    unschedulable?: boolean;
    taints?: { key: string; value?: string; effect: string }[];
  };
  status?: {
    addresses?: { type: string; address: string }[];
    conditions?: { type: string; status: string }[];
    allocatable?: Record<string, string>;
    capacity?: Record<string, string>;
    nodeInfo?: {
      kubeletVersion?: string;
      osImage?: string;
      containerRuntimeVersion?: string;
    };
  };
};

/** Подмножество Pod: только то, что нужно для запросов и счётчика подов по узлам. */
export type K8sPod = {
  metadata: { name: string; namespace: string };
  spec?: {
    nodeName?: string;
    containers?: { resources?: { requests?: Record<string, string> } }[];
  };
  status?: { phase?: string };
};

export type NodeRequests = { cpu: number; memory: number; pods: number };

/** Сумма requests контейнеров и число подов по узлу; завершённые поды не считаются. */
export const requestsByNode = (pods: K8sPod[]): Map<string, NodeRequests> => {
  const result = new Map<string, NodeRequests>();
  for (const pod of pods) {
    const node = pod.spec?.nodeName;
    const phase = pod.status?.phase;
    if (!node || phase === 'Succeeded' || phase === 'Failed') continue;
    const acc = result.get(node) ?? { cpu: 0, memory: 0, pods: 0 };
    for (const c of pod.spec?.containers ?? []) {
      acc.cpu += parseCpu(c.resources?.requests?.cpu);
      acc.memory += parseMemory(c.resources?.requests?.memory);
    }
    acc.pods += 1;
    result.set(node, acc);
  }
  return result;
};

export type NodeRow = {
  name: string;
  labels: Record<string, string>;
  internalIp?: string;
  externalIp?: string;
  ready: boolean;
  unschedulable: boolean;
  /** Роли из меток node-role.kubernetes.io/<роль>. */
  roles: string[];
  taints: string[];
  kubelet?: string;
  os?: string;
  cpuAllocatable: number;
  memoryAllocatable: number;
  gpuAllocatable: number;
  podsAllocatable: number;
  requests: NodeRequests;
};

export const toNodeRow = (
  node: K8sNode,
  requests: Map<string, NodeRequests>,
): NodeRow => {
  const labels = node.metadata.labels ?? {};
  const addr = (type: string) =>
    node.status?.addresses?.find(a => a.type === type)?.address;
  const alloc = node.status?.allocatable ?? node.status?.capacity ?? {};
  return {
    name: node.metadata.name,
    labels,
    internalIp: addr('InternalIP'),
    externalIp: addr('ExternalIP'),
    ready:
      node.status?.conditions?.some(
        c => c.type === 'Ready' && c.status === 'True',
      ) ?? false,
    unschedulable: Boolean(node.spec?.unschedulable),
    roles: Object.keys(labels)
      .filter(k => k.startsWith('node-role.kubernetes.io/'))
      .map(k => k.slice('node-role.kubernetes.io/'.length))
      .filter(Boolean)
      .sort(),
    taints: (node.spec?.taints ?? []).map(
      t => `${t.key}${t.value ? `=${t.value}` : ''}:${t.effect}`,
    ),
    kubelet: node.status?.nodeInfo?.kubeletVersion,
    os: node.status?.nodeInfo?.osImage,
    cpuAllocatable: parseCpu(alloc.cpu),
    memoryAllocatable: parseMemory(alloc.memory),
    gpuAllocatable: Number(alloc[GPU_RESOURCE] ?? 0),
    podsAllocatable: Number(alloc.pods ?? 0),
    requests: requests.get(node.metadata.name) ?? {
      cpu: 0,
      memory: 0,
      pods: 0,
    },
  };
};

export type NodePool = {
  /** Значение метки; undefined — узлы без метки. */
  key?: string;
  /** control-plane — мастер-узлы (при группировке по пулу), unlabeled — узлы без метки, pool — пул. */
  kind: 'control-plane' | 'unlabeled' | 'pool';
  nodes: NodeRow[];
  ready: number;
  cpu: number;
  memory: number;
  gpu: number;
  instanceTypes: string[];
  zones: string[];
};

/**
 * Порядок пулов хранилищ на странице кластера — как в меню: PostgreSQL, MongoDB, Valkey, Kafka,
 * RabbitMQ, RustFS. Тип пула — по аннотации кластера viva.company.com/<тип>-pool (пул по умолчанию
 * из формы кластера) или по имени (pg-fns, valkey1, …).
 */
const POOL_TYPES: { type: string; name: RegExp }[] = [
  { type: 'postgres', name: /^(pg|postgres)/ },
  { type: 'mongodb', name: /^mongo/ },
  { type: 'valkey', name: /^(valkey|redis)/ },
  { type: 'kafka', name: /^kafka/ },
  { type: 'rabbitmq', name: /^rabbit/ },
  { type: 'rustfs', name: /^(s3|rustfs|minio)/ },
];

export const poolRank = (
  key: string,
  annotations: Record<string, string> = {},
): number => {
  const i = POOL_TYPES.findIndex(
    t =>
      annotations[`viva.company.com/${t.type}-pool`] === key ||
      t.name.test(key),
  );
  return i === -1 ? POOL_TYPES.length : i;
};

const isControlPlane = (n: NodeRow) =>
  n.roles.includes('control-plane') || n.roles.includes('master');

/**
 * Группировка по метке. По метке пула (viva.io/pool): мастер-узлы, воркеры без пула, затем пулы
 * хранилищ по типу (poolRank) и прочие по алфавиту. По другой метке: значения по алфавиту, узлы
 * без метки — последней группой.
 */
export const groupNodes = (
  nodes: NodeRow[],
  label: string,
  annotations: Record<string, string> = {},
): NodePool[] => {
  const byPool = label === POOL_LABEL;
  const CP = '\u0000control-plane';
  const kindOf = (k: string | undefined): NodePool['kind'] => {
    if (k === CP) return 'control-plane';
    return k === undefined ? 'unlabeled' : 'pool';
  };
  const byKey = new Map<string | undefined, NodeRow[]>();
  for (const n of nodes) {
    const key =
      n.labels[label] ?? (byPool && isControlPlane(n) ? CP : undefined);
    byKey.set(key, [...(byKey.get(key) ?? []), n]);
  }
  const pools = [...byKey.entries()].map(([k, list]) => ({
    key: k === CP ? undefined : k,
    kind: kindOf(k),
    nodes: [...list].sort((a, b) => a.name.localeCompare(b.name)),
    ready: list.filter(n => n.ready).length,
    cpu: list.reduce((s, n) => s + n.cpuAllocatable, 0),
    memory: list.reduce((s, n) => s + n.memoryAllocatable, 0),
    gpu: list.reduce((s, n) => s + n.gpuAllocatable, 0),
    instanceTypes: [
      ...new Set(list.map(n => n.labels[INSTANCE_TYPE_LABEL]).filter(Boolean)),
    ].sort(),
    zones: [
      ...new Set(list.map(n => n.labels[ZONE_LABEL]).filter(Boolean)),
    ].sort(),
  }));
  if (!byPool) {
    return pools.sort((a, b) => {
      if (a.key === undefined) return 1;
      if (b.key === undefined) return -1;
      return a.key.localeCompare(b.key);
    });
  }
  const order = { 'control-plane': 0, unlabeled: 1, pool: 2 };
  return pools.sort(
    (a, b) =>
      order[a.kind] - order[b.kind] ||
      poolRank(a.key ?? '', annotations) - poolRank(b.key ?? '', annotations) ||
      (a.key ?? '').localeCompare(b.key ?? ''),
  );
};

/** Метки, встречающиеся хотя бы у одного узла, из списка кандидатов на группировку. */
export const availableGroupLabels = (nodes: NodeRow[]): string[] =>
  GROUP_LABELS.filter(label => nodes.some(n => label in n.labels));

/** Фильтр по имени или адресу (подстрока, без учёта регистра). */
export const matchesQuery = (node: NodeRow, query: string): boolean => {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [node.name, node.internalIp, node.externalIp].some(v =>
    v?.toLowerCase().includes(q),
  );
};
